import React, { useEffect, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import Database from "lucide-react/dist/esm/icons/database";
import { api } from "../lib/api";
import { QuietButton } from "./UI";

/**
 * LT-18c §1 (review A6, amended B-a/B-b/2a) — the taxonomy migration's own
 * report, rendered where Chris can read it. Everything shown comes off the
 * latest `migration_reports` document the boot wrote; nothing is derived.
 *
 *   outcome report_only   → the steps are what WOULD happen; AFTER is the
 *                           PROJECTED end state; the plan id is what Chris
 *                           approves by setting apply:<id>.
 *   outcome applied       → steps ran; AFTER is the ACTUAL state and is
 *                           checked against the projection.
 *   outcome plan_changed  → apply:<id> named a plan that no longer matches;
 *                           nothing ran; re-approve with the new id.
 *   outcome nothing_to_do → the plan is empty; apply is disarmed.
 *   outcome restored      → a snapshot was put back first.
 */
const OP_LABEL = {
  merge:                  "Merge duplicate label",
  merge_pointer_fix:      "Repair merged-into pointer",
  rekey:                  "Re-key by label",
  label_repoint:          "Label-valued reference → key",
  orphan_seed:            "Orphan reference → new row",
  canonical_seed:         "Seed canonical row",
  divisible:              "Set is_divisible",
  recipe_text:            "Recipe unit text → label",
  recipe_text_unresolved: "Recipe unit text — unresolved, left",
};

const OUTCOME = {
  report_only:      { text: "REPORT ONLY — nothing changed", cls: "bg-amber-50 text-amber-800" },
  applied:          { text: "APPLIED",                        cls: "bg-emerald-50 text-emerald-800" },
  plan_changed:     { text: "PLAN CHANGED — nothing changed", cls: "bg-red-50 text-red-800" },
  nothing_to_do:    { text: "nothing to do — apply disarmed", cls: "bg-emerald-50 text-emerald-800" },
  restored:         { text: "RESTORED from snapshot",         cls: "bg-sky-50 text-sky-800" },
  restore_disarmed: { text: "already restored — restore disarmed", cls: "bg-sky-50 text-sky-800" },
  restore_failed:   { text: "RESTORE FAILED",                 cls: "bg-red-50 text-red-800" },
  skipped:          { text: "skipped — lock held",            cls: "bg-ink-4/10 text-ink-3" },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" }) : "—");

function describe(s) {
  switch (s.op) {
    case "merge":
      return `'${s.label}': ${s.loser_key} (${s.loser_status_before}) archived in place, key kept, `
           + `merged into ${s.winner_key}; ${s.refs_repointed} reference(s) moved`;
    case "merge_pointer_fix":
      return `'${s.label}': merged_into ${s.from_key} → ${s.to_key}`;
    case "rekey":
      return `'${s.label}': ${s.old_key} → ${s.new_key}; ${s.refs_repointed} reference(s) repointed`;
    case "label_repoint":
      return `'${s.value}' → ${s.to_key} ('${s.to_label}'); ${s.refs_repointed} reference(s)`;
    case "orphan_seed":
      return `'${s.value}' matched nothing → new row ${s.new_key}; ${s.refs_repointed} reference(s)`;
    case "canonical_seed":
      return `${s.key} '${s.label}'${s.is_divisible != null ? ` divisible=${s.is_divisible}` : ""}`;
    case "divisible":
      return `${s.key} → ${s.value ? "divisible" : "not divisible"}`;
    case "recipe_text":
      return `'${s.text}' → '${s.new_label}' (via ${s.material_ids.length} linked material(s)) in ${s.library_items} library item(s), ${s.tenders} tender(s)`;
    case "recipe_text_unresolved":
      return `'${s.text}' in ${s.library_items} library item(s), ${s.tenders} tender(s) — ${s.reason}`;
    default:
      return JSON.stringify(s);
  }
}

function StateTable({ rows, isUnits, testId }) {
  return (
    <table className="w-full text-left" style={{ fontSize: 12 }} data-testid={testId}>
      <thead>
        <tr className="text-ink-4 uppercase tracking-wide" style={{ fontSize: 10 }}>
          <th className="py-1 pr-3 font-medium">Key</th>
          <th className="py-1 pr-3 font-medium">Label</th>
          <th className="py-1 pr-3 font-medium">Status</th>
          {isUnits && <th className="py-1 pr-3 font-medium">Divisible</th>}
          <th className="py-1 pr-3 font-medium text-right">Refs</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} className="border-t border-line/60">
            <td className="py-1 pr-3 font-mono">{r.key}</td>
            <td className="py-1 pr-3">{r.label}{r.merged_into_key ? <span className="text-ink-4"> → merged into {r.merged_into_key}</span> : null}</td>
            <td className="py-1 pr-3">{r.status}</td>
            {isUnits && <td className="py-1 pr-3">{r.is_divisible == null ? "—" : r.is_divisible ? "yes" : "no"}</td>}
            <td className="py-1 pr-3 text-right tabular-nums">{r.refs}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CollectionSection({ name, data }) {
  const isUnits = name === "material_units";
  const title = { material_units: "Units", material_categories: "Categories", suppliers: "Suppliers" }[name] || name;
  const [showState, setShowState] = useState(false);
  const steps = data.steps || [];
  const before = data.before || [];
  const after = data.after || [];
  const afterLabel = data.after_kind === "actual"
    ? `AFTER (actual${data.projection_matched === false ? " — DOES NOT MATCH PROJECTION" : data.projection_matched ? " — matches projection" : ""})`
    : "AFTER (projected — what apply would produce)";
  return (
    <div className="space-y-2" data-testid={`taxonomy-report-${name}`}>
      <div className="flex items-baseline gap-2">
        <h4 className="font-medium text-ink" style={{ fontSize: 13 }}>{title}</h4>
        <span className="text-ink-4" style={{ fontSize: 11 }} data-testid={`taxonomy-report-${name}-summary`}>
          {Object.entries(data.summary || {}).map(([k, v]) => `${v} ${k}`).join(" · ") || "no changes planned"}
        </span>
      </div>
      {name === "suppliers" && (
        <div className="text-ink-3" style={{ fontSize: 11.5 }} data-testid="taxonomy-report-suppliers-note">
          Re-key only (hyphen → underscore, the shared slug rule); found by name, never by key. No merges, no seeding.
          {" "}{data.rekey_active != null && <>{data.rekey_active} live and {data.rekey_archived} archived row(s) re-keyed{data.separator_only === false ? " — at least one re-key is more than a separator change" : ""}.</>}
          {" "}Key reference fields measured across materials and price history:{" "}
          <span className="font-mono">{JSON.stringify(data.reference_scan?.documents_with_a_supplier_key_field || {})}</span>
          {" "}(suppliers are linked by id, so 0 is the expected — and measured — answer).
        </div>
      )}
      {data.refused && (
        <div className="rounded border border-red-300 bg-red-50 text-red-900 p-3" style={{ fontSize: 12 }}
             data-testid={`taxonomy-report-${name}-refused`}>
          <div className="flex items-center gap-2 font-medium">
            <AlertTriangle size={14} /> REFUSED — {data.refused.reason}
          </div>
          <ul className="mt-1 ml-5 list-disc">
            {(data.refused.details || []).map((d, i) => (
              <li key={i} className="font-mono">{JSON.stringify(d)}</li>
            ))}
          </ul>
        </div>
      )}
      {steps.length > 0 && (
        <ol className="space-y-0.5" style={{ fontSize: 12 }} data-testid={`taxonomy-report-${name}-steps`}>
          {steps.map((s, i) => (
            <li key={i} className="flex gap-2">
              <span className={s.applied ? "text-emerald-700" : "text-amber-700"} style={{ minWidth: 58 }}>
                {s.applied ? "applied" : (s.op.endsWith("unresolved") ? "noted" : "planned")}
              </span>
              <span className="text-ink-3" style={{ minWidth: 190 }}>{OP_LABEL[s.op] || s.op}</span>
              <span className="text-ink">{describe(s)}</span>
            </li>
          ))}
        </ol>
      )}
      {isUnits && data.divisible?.fell_through_to_false?.length > 0 && (
        <div className="text-amber-800" style={{ fontSize: 12 }} data-testid="taxonomy-report-divisible-fallthrough">
          is_divisible fell through to <b>not divisible</b> (not in the table — set deliberately):{" "}
          {data.divisible.fell_through_to_false.map((x) => `${x.key} ('${x.label}')`).join(", ")}
        </div>
      )}
      {data.canonical?.label_present_under_key?.length > 0 && (
        <div className="text-amber-800" style={{ fontSize: 12 }}>
          Seed skipped — label already present under another key:{" "}
          {data.canonical.label_present_under_key.map((x) => `'${x.label}' wanted ${x.wanted_key}, held by ${x.held_by_key}`).join("; ")}
        </div>
      )}
      <button type="button" onClick={() => setShowState((v) => !v)}
              className="inline-flex items-center gap-1 text-ink-3 hover:text-ink" style={{ fontSize: 11 }}
              data-testid={`taxonomy-report-${name}-toggle-state`}>
        {showState ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        before / after ({before.length} → {after.length} rows{data.after_kind === "projected" ? ", projected" : ""})
      </button>
      {showState && (
        <div className="grid gap-4" style={{ gridTemplateColumns: "1fr 1fr" }}>
          <div><div className="text-ink-4 mb-1" style={{ fontSize: 10 }}>BEFORE</div><StateTable rows={before} isUnits={isUnits} testId={`taxonomy-report-${name}-before`} /></div>
          <div>
            <div className={`mb-1 ${data.projection_matched === false ? "text-red-700 font-medium" : "text-ink-4"}`} style={{ fontSize: 10 }} data-testid={`taxonomy-report-${name}-after-kind`}>{afterLabel}</div>
            <StateTable rows={after} isUnits={isUnits} testId={`taxonomy-report-${name}-after`} />
          </div>
        </div>
      )}
    </div>
  );
}

export function TaxonomyMigrationReport() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);

  const load = () => {
    api.get("/materials-taxonomy/migration-report")
      .then((r) => {
        setData(r.data);
        const rep = r.data?.report;
        const loud = rep && (["report_only", "plan_changed", "restored", "restore_disarmed", "restore_failed"].includes(rep.outcome) && !rep.plan_empty
          || rep.material_units?.refused || rep.material_categories?.refused || rep.suppliers?.refused || rep.outcome === "restore_disarmed");
        if (loud) setOpen(true);
      })
      .catch((e) => setErr(e?.message || "failed to load"));
  };
  useEffect(load, []);

  const rep = data?.report;
  const refused = rep && (rep.material_units?.refused || rep.material_categories?.refused || rep.suppliers?.refused);
  const oc = OUTCOME[rep?.outcome] || OUTCOME.report_only;
  const usableSnapshots = (data?.snapshots || []).filter((s) => s.kind);
  const legacySnapshots = (data?.snapshots || []).filter((s) => !s.kind);

  return (
    <div className="rounded border border-line bg-white p-4 space-y-3" data-testid="taxonomy-migration-report">
      <div className="flex items-center justify-between gap-3">
        <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-2 text-left flex-wrap"
                data-testid="taxonomy-report-toggle">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <Database size={14} className="text-ink-3" />
          <span className="font-medium text-ink" style={{ fontSize: 13 }}>Taxonomy migration — last run</span>
          {rep && (
            <span className={`px-2 py-0.5 rounded-full ${oc.cls}`} style={{ fontSize: 11 }} data-testid="taxonomy-report-outcome">
              {oc.text}
            </span>
          )}
          {refused && (
            <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-800 inline-flex items-center gap-1" style={{ fontSize: 11 }}
                  data-testid="taxonomy-report-refused-chip">
              <AlertTriangle size={11} /> refused
            </span>
          )}
        </button>
        <QuietButton icon={RefreshCw} onClick={load} data-testid="taxonomy-report-refresh">Refresh</QuietButton>
      </div>
      {err && <div className="text-red-700" style={{ fontSize: 12 }}>{err}</div>}
      {data?.lt15_job_type && (
        <div className="text-ink-3" style={{ fontSize: 12 }} data-testid="lt15-job-type-report">
          LT-15 job-type boot migration · database <span className="font-mono text-ink">{data.lt15_job_type.db}</span> · last boot {fmt(data.lt15_job_type.ran_at)} ·{" "}
          <span className="text-ink" data-testid="lt15-job-type-rewrites">{data.lt15_job_type.tenders_rewritten}</span> tender(s) rewritten,{" "}
          {data.lt15_job_type.templates_rewritten} template(s), {data.lt15_job_type.job_types_created} job type(s) created
        </div>
      )}
      {rep && (
        <div className="text-ink-3 space-y-0.5" style={{ fontSize: 12 }} data-testid="taxonomy-report-header">
          <div>
            database <span className="font-mono text-ink">{rep.db}</span> · ran {fmt(rep.ran_at)} · flag <span className="font-mono">{rep.mode}</span>
            {rep.writes != null && <> · {rep.writes} write(s)</>}
          </div>
          <div>
            plan id <span className="font-mono text-ink" data-testid="taxonomy-report-plan-id">{rep.plan_id}</span>
            {rep.plan_empty ? " (empty plan)" : ""}
            {rep.approved_plan_id && <> · approved <span className="font-mono">{rep.approved_plan_id}</span></>}
            {rep.snapshot ? (
              <> · snapshot <span className="font-mono" data-testid="taxonomy-report-snapshot-id">{rep.snapshot.snapshot_id}</span>
                {rep.snapshot.reused ? " (reused)" : ` taken ${fmt(rep.snapshot.taken_at)}`}
                {" "}({rep.snapshot.counts?.material_units} units, {rep.snapshot.counts?.material_categories} categories, {rep.snapshot.counts?.materials} materials)</>
            ) : " · no snapshot (nothing to change)"}
          </div>
          {rep.last_applied && (
            <div data-testid="taxonomy-report-last-applied">
              last applied plan <span className="font-mono">{rep.last_applied.plan_id}</span> at {fmt(rep.last_applied.applied_at)}
            </div>
          )}
        </div>
      )}
      {rep?.last_applied?.snapshot_id && (
        <div className="rounded border border-sky-300 bg-sky-50 text-sky-900 p-3" style={{ fontSize: 12 }} data-testid="taxonomy-report-restore-point">
          <b>Restore point for that apply: <span className="font-mono">{rep.last_applied.snapshot_id}</span></b>
          {" "}— the state immediately before plan {rep.last_applied.plan_id} ran. To undo it, set{" "}
          <span className="font-mono">LT18C_MIGRATION_MODE=restore:{rep.last_applied.snapshot_id}</span> in Manage Publishes → Secrets and publish.
          Restore runs once per snapshot and then disarms itself.
        </div>
      )}
      {rep && !rep.plan_empty && rep.outcome !== "applied" && !(rep.last_applied?.plan_id === rep.plan_id) && (
        <div className="rounded border border-line bg-surface-2/40 text-ink-2 p-3" style={{ fontSize: 12 }} data-testid="taxonomy-report-restore-point-none">
          <b>Restore point for this plan: none yet.</b>{" "}
          {rep.snapshot?.snapshot_id ? <>The report-only snapshot <span className="font-mono">{rep.snapshot.snapshot_id}</span> above is not it. </> : null}
          When <span className="font-mono">apply:{rep.plan_id}</span> runs, a fresh snapshot is taken before the first write and named here as "Restore point for that apply". Keep that id.
        </div>
      )}
      {rep && usableSnapshots.length === 0 && legacySnapshots.length > 0 && (
        <div className="rounded border border-amber-300 bg-amber-50 text-amber-900 p-3" style={{ fontSize: 12 }} data-testid="taxonomy-report-no-usable-snapshot">
          <b>No usable restore point on this database yet.</b>{" "}
          {legacySnapshots.map((s) => s.snapshot_id).join(" and ")} {legacySnapshots.length === 1 ? "is" : "are"} 9fdb4a9-shape (no full documents) and cannot be restored. The first usable one arrives with the apply.
        </div>
      )}
      {!rep && !err && <div className="text-ink-4" style={{ fontSize: 12 }}>No migration report recorded yet.</div>}
      {open && rep && (
        <div className="space-y-5 pt-1">
          {rep.outcome === "plan_changed" && (
            <div className="rounded border border-red-300 bg-red-50 text-red-900 p-3" style={{ fontSize: 12 }} data-testid="taxonomy-report-plan-changed-banner">
              <b>PLAN CHANGED — re-approve.</b> {rep.note} The database moved since the approved plan was computed; nothing was changed.
            </div>
          )}
          {rep.outcome === "report_only" && !rep.plan_empty && (
            <div className="rounded border border-amber-300 bg-amber-50 text-amber-900 p-3" style={{ fontSize: 12 }} data-testid="taxonomy-report-report-only-banner">
              Report-only: the steps below are what the migration <b>would</b> do; AFTER is the projected end state. Nothing in the taxonomy was changed.
              To run exactly this plan, set <span className="font-mono">LT18C_MIGRATION_MODE=apply:{rep.plan_id}</span> in Manage Publishes → Secrets and publish again.
              {rep.note && <div className="mt-1">{rep.note}</div>}
            </div>
          )}
          {rep.outcome === "nothing_to_do" && (
            <div className="rounded border border-emerald-300 bg-emerald-50 text-emerald-900 p-3" style={{ fontSize: 12 }} data-testid="taxonomy-report-disarmed-banner">
              {rep.note}
            </div>
          )}
          {rep.restore && (
            <div className={`rounded border p-3 ${rep.restore.error ? "border-red-300 bg-red-50 text-red-900" : "border-sky-300 bg-sky-50 text-sky-900"}`} style={{ fontSize: 12 }} data-testid="taxonomy-report-restore">
              {rep.restore.error
                ? <>Restore of <span className="font-mono">{rep.restore.snapshot_id}</span> failed: {rep.restore.error}</>
                : rep.restore.disarmed
                  ? <><b>Restore disarmed.</b> Snapshot <span className="font-mono">{rep.restore.snapshot_id}</span> was already restored {fmt(rep.restore.restored_at)} and nothing has been applied since — nothing was changed on this boot. Remove the flag.</>
                  : <>Restored snapshot <span className="font-mono">{rep.restore.snapshot_id}</span> (taken {fmt(rep.restore.taken_at)}):{" "}
                      {Object.entries(rep.restore.restored || {}).map(([k, v]) => `${v} ${k}`).join(", ")}
                      {Object.values(rep.restore.removed || {}).some((a) => a.length) && <> · removed rows the snapshot did not have: {JSON.stringify(rep.restore.removed)}</>}
                      {" "}· the state before this restore is kept as snapshot <span className="font-mono">{rep.restore.pre_restore_snapshot_id}</span>
                    </>}
            </div>
          )}
          <CollectionSection name="material_units" data={rep.material_units || {}} />
          <CollectionSection name="material_categories" data={rep.material_categories || {}} />
          {rep.suppliers && <CollectionSection name="suppliers" data={rep.suppliers} />}
          {data?.snapshots?.length > 0 && (
            <div className="text-ink-4 space-y-0.5" style={{ fontSize: 11 }} data-testid="taxonomy-report-snapshots">
              <div>Restore snapshots held (same database — protects against the migration's logic, not the database). Report-only snapshots keep the newest three; apply and restore snapshots are never pruned. To put one back: <span className="font-mono">LT18C_MIGRATION_MODE=restore:&lt;snapshot id&gt;</span></div>
              {data.snapshots.map((s) => (
                <div key={s.snapshot_id} className={`font-mono ${s.snapshot_id === rep.last_applied?.snapshot_id ? "text-sky-800 font-medium" : ""}`}>
                  {s.snapshot_id} · {s.kind || `${s.mode} (legacy 9fdb4a9 shape — not restorable)`} · {s.kind === "restore" ? `pre-restore of ${s.restores_snapshot_id}` : `plan ${s.plan_id || "—"}`} · {fmt(s.taken_at)} · {s.counts?.material_units}u / {s.counts?.material_categories}c / {s.counts?.suppliers ?? "—"}s / {s.counts?.materials}m
                  {s.snapshot_id === rep.last_applied?.snapshot_id ? " ← restore point for the last apply" : ""}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
